import {ChangeDetectionStrategy, Component, computed, effect, inject, signal} from '@angular/core';
import {rxResource} from '@angular/core/rxjs-interop';
import {firstValueFrom} from 'rxjs';
import {
  AuditInfo,
  CriteriaWeightingResponse,
  EvaluationCriteriaResponse,
  PairwiseComparisonRequest,
  ProjectRoleType,
  ProjectStepType,
  UpsertCriteriaWeightingRequest,
} from '@frontend/shared-api';
import {EvaluationCriteria} from '@frontend/directus';
import {ToastService} from '@frontend/toast';
import {ProjectStepBaseReactive} from '../../../project-base-step/project-base-step-reactive.component';
import {BadgeListComponent} from '../../../../../../shared/components/list/badge-list/badge-list.component';
import {BadgeListItem} from '../../../../../../shared/components/list/badge-list/badge-list.types';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {StepDotMenuComponent} from '../../../../../../shared/components/step-dot-menu/step-dot-menu.component';
import {CardTitleComponent} from '../../../../../../shared/components/header/card-title/card-title.component';
import {CriteriaWeightingXComponent} from '../components/criteria-weighting-x/criteria-weighting-x.component';
import {PairwiseComparisonX, PairwiseItemX} from '../evaluation-criteria-x.types';
import {Step41EvaluationCriteriaXService} from './step-4-1-evaluation-criteria-x.service';

@Component({
  selector: 'app-step-4-1-evaluation-criteria-x',
  imports: [
    BadgeListComponent,
    LoadingComponent,
    StepDotMenuComponent,
    CardTitleComponent,
    CriteriaWeightingXComponent,
  ],
  templateUrl: './step-4-1-evaluation-criteria-x.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Step41EvaluationCriteriaXComponent extends ProjectStepBaseReactive {
  protected readonly ProjectRoleType = ProjectRoleType;
  protected readonly ProjectStepType = ProjectStepType;

  private readonly service = inject(Step41EvaluationCriteriaXService);
  private readonly toast = inject(ToastService);

  protected readonly isSaving = signal(false);
  private readonly initialLoadDone = signal(false);

  private readonly criteriaResource = rxResource({
    params: () => this.projectId(),
    stream: ({params: projectId}) => this.service.loadCriteria(projectId),
  });

  private readonly directusCriteriaResource = rxResource({
    stream: () => this.service.loadDirectusCriteria(),
  });

  private readonly comparisonsResource = rxResource({
    params: () => this.projectId(),
    stream: ({params: projectId}) => this.service.loadComparisons(projectId),
  });

  private readonly weightingResource = rxResource({
    params: () => this.projectId(),
    stream: ({params: projectId}) => this.service.loadWeighting(projectId),
  });

  private readonly resourcesLoading = computed<boolean>(
    () =>
      this.isLoadingState() ||
      this.criteriaResource.status() === 'loading' ||
      this.directusCriteriaResource.status() === 'loading' ||
      this.comparisonsResource.status() === 'loading' ||
      this.weightingResource.status() === 'loading',
  );

  protected readonly loading = computed<boolean>(
    () => !this.initialLoadDone() && this.resourcesLoading(),
  );

  private readonly criteria = computed<EvaluationCriteriaResponse[]>(
    () => this.criteriaResource.value() ?? [],
  );

  private readonly directusCriteriaById = computed<Map<string, EvaluationCriteria>>(
    () =>
      new Map(
        (this.directusCriteriaResource.value() ?? []).map((criteria) => [criteria.id, criteria]),
      ),
  );

  protected readonly badgeListItems = computed<BadgeListItem[]>(() => {
    const directusCriteriaById = this.directusCriteriaById();

    return this.criteria().map((criteria) => ({
      key: criteria.directusId,
      identifier: `EC${criteria.ecNumber}`,
      translations: directusCriteriaById.get(criteria.directusId)?.translations ?? [],
      originalItem: criteria,
    }));
  });

  protected readonly pairwiseItems = computed<PairwiseItemX[]>(() => {
    const directusCriteriaById = this.directusCriteriaById();

    return this.criteria().map((criteria) => ({
      id: criteria.id,
      sortNumber: criteria.ecNumber,
      identifier: `EC${criteria.ecNumber}`,
      translations: directusCriteriaById.get(criteria.directusId)?.translations ?? [],
    }));
  });

  protected readonly comparisons = computed<PairwiseComparisonX[]>(
    () => this.comparisonsResource.value()?.comparisons ?? [],
  );

  protected readonly weighting = computed<CriteriaWeightingResponse | null>(
    () => this.weightingResource.value() ?? null,
  );

  protected readonly auditInfo = computed<AuditInfo | null>(
    () => this.comparisonsResource.value()?.auditInfo ?? null,
  );

  constructor() {
    super();

    effect(() => {
      if (!this.resourcesLoading()) {
        this.initialLoadDone.set(true);
      }
    });
  }

  async onComparisonsSave(comparisons: PairwiseComparisonRequest[]): Promise<void> {
    this.isSaving.set(true);

    try {
      const saved = await firstValueFrom(
        this.service.saveComparisons(this.projectId()!, comparisons),
      );
      this.comparisonsResource.set(saved);
      this.weightingResource.reload();
      this.criteriaResource.reload();
      await this.stepState.loadStepStatuses(this.projectId()!);
      await this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
    } finally {
      this.isSaving.set(false);
    }
  }

  async onWeightingSave(request: UpsertCriteriaWeightingRequest): Promise<void> {
    this.isSaving.set(true);

    try {
      const saved = await firstValueFrom(this.service.saveWeighting(this.projectId()!, request));
      this.weightingResource.set(saved);
      this.criteriaResource.reload();
      await this.stepState.loadStepStatuses(this.projectId()!);
      await this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
    } finally {
      this.isSaving.set(false);
    }
  }
}
